import { usePickerTarget } from '@/components/modals/usePickerTarget';
import type { ThunkDispatch } from 'redux-thunk';
import type { AnyAction } from 'redux';
import type { RootState } from '@/store/store';
import { addTypemarkPickerItems } from '@/store/actions/typemarkPicker';
import { existingPickerItems, type PickerItem } from '@/components/modals/classificationPicker';
import { Pencil, Plus, List as ListIcon } from 'lucide-react';
import React, { Suspense, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SkeletonGroup } from '@/components/ui/Skeleton';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/Button';
import { ActionBar } from '@/components/ui/ActionBar';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import DataTable from '../../../../components/DataTable';
import PickValueWithClassificationModal from '../../../../components/modals/PickValueWithClassificationModal';
import {
  addProperty,
  removeProperty,
  removeTechnicalReportItem,
  updateTypeMarkBasicData,
  addSolution,
  removeSolution,
  addColor,
  removeColor,
  applyTemplate,
  removePropertyMulti,
  updateTypeMarkBasicDataMulti,
  removeSolutionMulti,
  removeTechnicalReportItemMulti,
  addPropertyMulti,
  addColorMulti,
  removeColorMulti,
  removePropertyDiffMulti,
  removeTechnicalReportItemDiffMulti,
  addSolutionMulti,
  removeNonCommonSolutions,
  removeNonCommonColors,
  removeReferenceProduct,
  removeReferenceProductMulti,
} from '@/store/actions/typemark';
import TypeMarkModal from './modals/TypeMarkModal';
import { isEmptyArray } from '../../../../utils/array';
import TypeMarkComponentTable from './TypeMarkComponentTable';
import ColorsModal from './modals/ColorsModal';
import SolutionPropertyDefinitionModal from '../../../database/solution/components/modals/SolutionPropertyDefinitionModal';
import InstanceTable from './InstanceTable';
import View from '../../../geometry/view';
import SolutionPickerModal from '../../../../components/modals/SolutionPickerModal';
import { useQueryClient } from 'react-query';
import { ImageRegular, TextGrammarCheckmark20Regular } from '@fluentui/react-icons';
import TemplateModal from './modals/TemplateModal';
import GeometrySwitch from './GeometrySwitch';
import { ErrorBoundary } from 'react-error-boundary';
import WrongGeometryFallback from './WrongGeometryFallback';
import ActionsDropdown from './actionsDropdown/ActionsDropdown';
import StatusDropdown from './StatusDropdown';
import { useTypemarkContext } from '../TypemarkContext';
import TyperMarkCompositionLayersTable from './typeMarkCompositionLayersTable/TyperMarkCompositionLayersTable';
import TypeMarkCompositionView from './TypeMarkCompositionView';
import { EmptyState } from '@/components/ui/EmptyState';
import { Layers as LayersIcon } from 'lucide-react';

const GeometryContent = ({ data, geometry, loadingGeo, activeItemId }: any) => {
  const viewMode = data.view_mode || (data.is_geo ? 'geometry' : 'image');
  const currentViewMode = viewMode === 'layers' ? 'geometry' : viewMode;

  if (currentViewMode === 'geometry') {
    if (data.classification?.define_composition) {
      return (
        <div className="aspect-[4/3] flex items-center justify-center bg-neutral-25/40">
          <TypeMarkCompositionView instanceTypeId={activeItemId} width={480} height={360} />
        </div>
      );
    }
    if (loadingGeo) {
      return (
        <div className="aspect-[4/3] flex items-center justify-center bg-neutral-25/40">
          <SpinnerOverlay />
        </div>
      );
    }
    if (geometry?.geo) {
      return (
        <div className="aspect-[4/3] flex items-center justify-center bg-neutral-25/40">
          <ErrorBoundary fallback={<WrongGeometryFallback />}>
            <View
              geometry={geometry.geo}
              canvasId="three"
              cameraType={geometry.perspective ? 'perspective' : 'orthogonal'}
              position={geometry.position}
              zoom={geometry.zoom}
              layers={geometry.layers}
            />
          </ErrorBoundary>
        </div>
      );
    }
    return (
      <div className="aspect-[4/3] flex flex-col items-center justify-center bg-neutral-25/40">
        <div className="h-12 w-12 rounded-full bg-neutral-100 flex items-center justify-center mb-3">
          <LayersIcon size={20} className="text-neutral-400" />
        </div>
        <div className="text-sm font-medium text-neutral-700">Bez definovaných vrstev</div>
        <p className="text-xs text-neutral-500 mt-1">Přidejte geometrii nebo vrstvy</p>
      </div>
    );
  }

  // Image mode
  if (data.image_url) {
    return (
      <div className="aspect-[4/3] flex items-center justify-center bg-neutral-25/40">
        <img src={data.image_url} className="max-h-full max-w-full object-contain" />
      </div>
    );
  }

  return (
    <div className="aspect-[4/3] flex flex-col items-center justify-center bg-neutral-25/40">
      <div className="h-12 w-12 rounded-full bg-neutral-100 flex items-center justify-center mb-3">
        <ImageRegular className="size-5 text-neutral-400" />
      </div>
      <div className="text-sm font-medium text-neutral-700">Žádný obrázek</div>
      <p className="text-xs text-neutral-500 mt-1">Nahrajte obrázek přes tlačítko úprav</p>
    </div>
  );
};

const detailData = ({ title, type_mark, description, classification }: any = {}) => [
  { name: 'Označení', value: type_mark },
  { name: 'Název', value: title },
  { name: 'Zatřídění', value: classification?.title },
  { name: 'Popis', value: description },
];

const prepareDataForComponentSet = (component_set, isMulti) => {
  if (!component_set) return [];
  const result = [];
  component_set.forEach((component) => {
    const isDifference = component.diff_solution_flag && isMulti;

    const values = {
      title: component.title,
      id: component.id,
      solution: null,
      color: null,
    };
    if (component.solution_set.length < 1) {
      result.push({ ...values, rowSpan: isDifference ? 2 : 1 });
    }
    component.solution_set.forEach((solution, i) => {
      if (i === 0) {
        result.push({
          ...values,
          solution,
          color: solution?.appearance,
          rowSpan: isDifference ? component.solution_set.length + 1 : component.solution_set.length,
        });
      } else {
        result.push({
          ...values,
          solution,
          color: solution?.appearance,
          rowSpan: 0,
        });
      }
    });

    if (isDifference) {
      result.push({ ...values, diffRow: true });
    }
  });

  return result;
};

export default function TypeMarkDetailView({
  activeItemId,
  permission,
  isRevitView,
}: {
  activeItemId: any;
  permission: any;
  isRevitView?: any;
  data?: any;
  geometry?: any;
}) {
  const [visibleAddPropertyDialog, setVisibleAddPropertyDialog] = useState(false);
  const [visibleAddPropertyValueDialog, setVisibleAddPropertyValueDialog] = useState(false);
  const [visibleAddPTechnicalReportItemDialog, setVisibleAddPTechnicalReportItemDialog] =
    useState(false);
  const [visibleBasicEditDialog, setVisibleBasicEditDialog] = useState(false);
  const [visibleAddComponent, setVisibleAddComponent] = useState(false);
  const [activeComponentId, setActiveComponentId] = useState(null);

  const [classifiedSolutionId, setClassifiedSolutionId] = useState(null);

  const [visibleColorsDialog, setVisibleColorsDialog] = useState(false);
  const [propertyIdForSetValue, setPropertyIdForSetValue] = useState<any>(false);
  const [visibleAddPropertySetDialog, setVisibleAddPropertySetDialog] = useState(false);
  const [visibleAddReferenceProductDialog, setVisibleAddReferenceProductDialog] = useState(false);
  const { selected } = useTypemarkContext() || {};

  const isMultiMode = Object.keys(selected || {}).length > 1;
  const pickerTarget = usePickerTarget(isMultiMode ? Object.keys(selected) : [activeItemId]);

  const [templatesModalOpen, setTemplatesModalOpen] = useState(false);
  const [visibleAddTechnicalReportItemSetDialog, setVisibleAddTechnicalReportItemSetDialog] =
    useState(false);
  const {
    detail: data,
    loading,
    loadingGeo,
    loadingInstances,
    geometry,
  } = useSelector((state) => state.typeMark);

  const queryClient = useQueryClient();

  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();

  const handleAddPropertySet = (items: PickerItem[]) =>
    dispatch(addTypemarkPickerItems(pickerTarget, 'propertySets', items));

  const handleAddTechnicalReportItemSet = (items: PickerItem[]) =>
    dispatch(addTypemarkPickerItems(pickerTarget, 'technicalReportSets', items));

  const handleAddProperty = (items: PickerItem[]) =>
    dispatch(addTypemarkPickerItems(pickerTarget, 'properties', items));

  const handleRemoveProperty = (id, diffValuesAction) => {
    if (diffValuesAction) {
      dispatch(removePropertyDiffMulti(Object.keys(selected), undefined));
      return;
    }

    const selectedProperty = data?.property_set?.find(
      (property) => property.property_definition.id === id
    ) as any;

    if (!selectedProperty) return;

    if (isMultiMode) {
      dispatch(
        removePropertyMulti(Object.keys(selected), {
          classified_property_definition_id: selectedProperty.property_definition.classified_id,
        })
      );
    } else {
      dispatch(
        removeProperty(activeItemId, {
          classified_property_definition: selectedProperty.property_definition.classified_id,
        })
      );
    }
  };

  const handleAddTechnicalReportItem = (items: PickerItem[]) =>
    dispatch(addTypemarkPickerItems(pickerTarget, 'technicalReports', items));

  const handleRemoveTechnicalReportItem = (id, diffValuesAction) => {
    if (diffValuesAction) {
      dispatch(removeTechnicalReportItemDiffMulti(Object.keys(selected)));
      return;
    }

    if (isMultiMode) {
      dispatch(
        removeTechnicalReportItemMulti(Object.keys(selected), {
          classified_technical_report_item_id: id,
        })
      );
    } else {
      dispatch(
        removeTechnicalReportItem(activeItemId, {
          classified_technical_report_item: id,
        })
      );
    }
  };

  const handleAddReferenceProduct = (items: PickerItem[]) =>
    dispatch(addTypemarkPickerItems(pickerTarget, 'products', items));

  const handleRemoveReferenceProduct = (id) => {
    if (isMultiMode) {
      dispatch(
        removeReferenceProductMulti(Object.keys(selected), {
          classified_product_id: id,
        })
      );
    } else {
      dispatch(
        removeReferenceProduct(activeItemId, {
          classified_product: id,
        })
      );
    }
  };

  const handleAddPropertyValue = (selectedValue) => {
    if (!propertyIdForSetValue) return;

    const selectedProperty = data?.property_set?.find(
      (property) => property.property_definition.id === propertyIdForSetValue
    ) as any;

    if (!selectedProperty) return;

    if (isMultiMode) {
      dispatch(
        addPropertyMulti(Object.keys(selected), {
          classified_property_definition_id: selectedProperty.property_definition.classified_id,
          ...selectedValue,
        })
      );
    } else {
      dispatch(
        addProperty(activeItemId, {
          classified_property_definition: selectedProperty.property_definition.classified_id,
          ...selectedValue,
        })
      );
    }

    setVisibleAddPropertyValueDialog(false);
    setPropertyIdForSetValue(null);
  };

  const handleCloseTypeMarkDialog = () => {
    setVisibleBasicEditDialog(false);
  };

  const handleSubmitTypeMarkDialog = ({ values }) => {
    if (isMultiMode) {
      dispatch(
        updateTypeMarkBasicDataMulti(
          Object.keys(selected),
          { description: values.description ?? '' },
          queryClient
        )
      );
    } else {
      const transformed = {
        ...values,
        description: values.description === '' ? null : values.description,
      };
      dispatch(updateTypeMarkBasicData(activeItemId, transformed, queryClient));
    }

    setVisibleBasicEditDialog(false);
  };

  const handleRemoveSolution = (classified_solution_id, component_set_id) => {
    if (isMultiMode) {
      dispatch(
        removeSolutionMulti(Object.keys(selected), {
          classified_solution_id,
          classification_id: activeItemId,
          component_id: component_set_id,
        })
      );
    } else {
      dispatch(removeSolution(activeItemId, classified_solution_id, component_set_id));
    }
  };

  // eslint-disable-next-line no-shadow
  const handleRemoveColor = (classified_solution, component, appearance) => {
    if (isMultiMode) {
      dispatch(
        removeColorMulti(Object.keys(selected), {
          classified_solution_id: classified_solution,
          appearance_id: appearance,
          component_id: component,
        })
      );
    } else {
      dispatch(removeColor(activeItemId, classified_solution, component, appearance));
    }
  };

  const handleDeleteNonCommonColors = (component, classified_solution) => {
    dispatch(
      removeNonCommonColors(Object.keys(selected), {
        component_id: component,
        classified_solution_id: classified_solution,
      })
    );
  };

  const handleDeleteNonCommonSolutions = (component) => {
    dispatch(
      removeNonCommonSolutions(Object.keys(selected), {
        component_id: component,
      })
    );
  };

  const handleAddComponent = (pickedSolution) => {
    if (isMultiMode) {
      dispatch(addSolutionMulti(Object.keys(selected), activeComponentId, pickedSolution?.id));
    } else {
      dispatch(addSolution(activeItemId, activeComponentId, pickedSolution?.id));
    }

    setVisibleAddComponent(false);
  };

  const handleSubmitColorsDialog = (values) => {
    if (isMultiMode) {
      dispatch(
        addColorMulti(
          Object.keys(selected),
          classifiedSolutionId,
          activeComponentId,
          values.appearance
        )
      );
    } else {
      dispatch(addColor(activeItemId, classifiedSolutionId, activeComponentId, values.appearance));
    }

    setVisibleColorsDialog(false);
  };

  const handlePickTemplate = (template) => {
    dispatch(applyTemplate(activeItemId, template.id));
    setTemplatesModalOpen(false);
  };

  const filterDiffProperties = () => {
    const properties = data?.property_set;

    const filtered = properties.filter((property) => !property.property_definition.diff_flag);

    if (properties.length > filtered.length) {
      return filtered
        .map((item) => ({
          ...item.property_definition,
          title: `${item.property_definition.code} ${item.property_definition.title} - `,
          value: item.value,
        }))
        .concat([{ title: 'Různé', value: '', diffMulti: true }]);
    }
    return filtered.map((item) => ({
      ...item.property_definition,
      title: `${item.property_definition.code} ${item.property_definition.title} - `,
      value: item.value,
    }));
  };

  const filterDiffTechnicalReportItems = () => {
    const technicalReportItems = data?.technical_report_item_set;

    const filtered = technicalReportItems.filter(
      (technicalReportItem) => !technicalReportItem.diff_flag
    );

    if (technicalReportItems.length > filtered.length) {
      return filtered
        .map((item) => ({
          ...item,
          title: `${item.code} ${item.title} - `,
          value: item.value,
        }))
        .concat([{ title: 'Různé', value: '', diffMulti: true }]);
    }
    return filtered.map((item) => ({
      ...item,
      title: `${item.code} ${item.title} - `,
      value: item.value,
    }));
  };

  // eslint-disable-next-line no-constant-condition
  if (loading) {
    return (
      <div className="flex-1 flex justify-center items-center">
        <SpinnerOverlay />
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto">
        <ActionBar
          left={
            <>
              <h3 className="text-sm font-semibold tracking-tight">{data?.title}</h3>
              {data?.type_mark && (
                <span className="font-mono text-[11px] text-neutral-500 bg-neutral-50 border border-neutral-150 rounded px-1.5 py-0.5">
                  {data.type_mark}
                </span>
              )}
            </>
          }
          right={
            <>
              <StatusDropdown selectedDetail={data} />
              <ActionsDropdown key={0} selectedDetail={data} />
            </>
          }
        />

        <div className="px-8 py-5">
          <div className="flex gap-5 items-start">
            <Card className="overflow-hidden w-[480px] shrink-0">
              {isMultiMode ? (
                <div className="aspect-[4/3] flex items-center justify-center text-neutral-500 gap-2">
                  <TextGrammarCheckmark20Regular />
                  <p className="text-sm">Vybráno více položek</p>
                </div>
              ) : (
                <>
                  <CardHeader className="px-4 py-0 h-9 flex-row items-center space-y-0 [&>*]:w-full">
                    <GeometrySwitch typeMark={data} geometry={geometry} />
                  </CardHeader>
                  <CardContent className="p-0">
                    <GeometryContent
                      data={data}
                      geometry={geometry}
                      loadingGeo={loadingGeo}
                      activeItemId={activeItemId}
                    />
                  </CardContent>
                </>
              )}
            </Card>

            <Card className="min-w-0 flex-1">
              <CardHeader className="px-4 py-0 h-9 flex-row items-center justify-between space-y-0">
                <CardTitle>Detail</CardTitle>
                {permission === 2 && data && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-neutral-300 hover:text-neutral-700"
                    data-testid="typemark-detail-edit-btn"
                    onClick={() => setVisibleBasicEditDialog(true)}
                  >
                    <Pencil size={12} />
                  </Button>
                )}
              </CardHeader>
              <CardContent className="p-0">
                <Suspense fallback={<SkeletonGroup />}>
                  <div className="px-4 py-2 divide-y divide-neutral-100">
                    {detailData(data || {}).map((item, i) => (
                      <div key={i} className="grid grid-cols-[90px_1fr] gap-2 py-2 items-baseline">
                        <div className="text-xs text-neutral-500 truncate">{item.name}:</div>
                        <div className="text-sm text-neutral-900 break-words min-w-0">
                          {item.value || '—'}
                        </div>
                      </div>
                    ))}
                  </div>
                </Suspense>
              </CardContent>
            </Card>
          </div>

          {/* Composition layers — only for skladba */}
          {data?.classification?.define_composition && (
            <TyperMarkCompositionLayersTable instanceTypeId={activeItemId} />
          )}

          <div className="flex flex-col">
            <div className="mt-6">
              <div className="flex items-center justify-between pt-3 pb-1 border-b border-neutral-150 mb-4">
                <span className="text-sm font-semibold tracking-tight">Komponenty</span>
              </div>
              {data && isEmptyArray(data.component_set) && (
                <TypeMarkComponentTable
                  columns={[
                    { title: 'Název', key: 'title' },
                    { title: 'Řešení', key: 'solution' },
                    { title: 'Vzhled', key: 'color' },
                    { title: 'id', key: 'solutionId' },
                    { title: 'id', key: 'componentId' },
                  ]}
                  data={prepareDataForComponentSet(data?.component_set, isMultiMode)}
                  onDeleteSolution={handleRemoveSolution}
                  onEditColor={(componentId, classifiedSolutionId) => {
                    setActiveComponentId(componentId);
                    // setPickedSolutionId(solutionId);
                    setClassifiedSolutionId(classifiedSolutionId);
                    setVisibleColorsDialog(true);
                  }}
                  onDeleteColor={handleRemoveColor}
                  onDeleteDiffColor={handleDeleteNonCommonColors}
                  onAdd={(id) => {
                    setVisibleAddComponent(true);
                    setActiveComponentId(id);
                  }}
                  onDeleteNonCommonColors={handleDeleteNonCommonColors as any}
                  onDeleteNonCommonSolutions={handleDeleteNonCommonSolutions}
                  onDelete={() => {}}
                />
              )}
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between pt-3 pb-1 border-b border-neutral-150 mb-4">
                <span className="text-sm font-semibold tracking-tight">Vlastnosti</span>
                {permission === 2 && data && (
                  <div className="flex items-center gap-1">
                    <button
                      className="h-7 w-7 rounded flex items-center justify-center text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50 transition-colors"
                      data-testid="typemark-add-property-btn"
                      onClick={() => setVisibleAddPropertyDialog(true)}
                    >
                      <Plus size={14} />
                    </button>
                    <button
                      className="h-7 w-7 rounded flex items-center justify-center text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50 transition-colors"
                      data-testid="typemark-add-property-set-btn"
                      onClick={() => setVisibleAddPropertySetDialog(true)}
                    >
                      <ListIcon size={14} />
                    </button>
                  </div>
                )}
              </div>
              {data && isEmptyArray(data.property_set) && (
                <DataTable
                  columns={[
                    { title: 'Název', key: 'title' },
                    { title: 'Hodnota', key: 'value' },
                    { title: 'Popis', key: 'description' },
                    { title: 'Norma', key: 'biblios', list: true },
                    { title: 'id', key: 'id' },
                  ]}
                  data={filterDiffProperties()}
                  onDelete={handleRemoveProperty}
                  onEdit={(id) => {
                    setPropertyIdForSetValue(id);
                    setVisibleAddPropertyValueDialog(true);
                  }}
                />
              )}
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between pt-3 pb-1 border-b border-neutral-150 mb-4">
                <span className="text-sm font-semibold tracking-tight">
                  Položky technické zprávy
                </span>
                {permission === 2 && data && (
                  <div className="flex items-center gap-1">
                    <button
                      className="h-7 w-7 rounded flex items-center justify-center text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50 transition-colors"
                      data-testid="typemark-add-report-item-btn"
                      onClick={() => setVisibleAddPTechnicalReportItemDialog(true)}
                    >
                      <Plus size={14} />
                    </button>
                    <button
                      className="h-7 w-7 rounded flex items-center justify-center text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50 transition-colors"
                      data-testid="typemark-add-report-item-set-btn"
                      onClick={() => setVisibleAddTechnicalReportItemSetDialog(true)}
                    >
                      <ListIcon size={14} />
                    </button>
                  </div>
                )}
              </div>
              {data && isEmptyArray(data.technical_report_item_set) && (
                <DataTable
                  columns={[
                    { title: 'Název', key: 'title' },
                    { title: 'Popis', key: 'description' },
                    { title: 'id', key: 'id' },
                  ]}
                  data={filterDiffTechnicalReportItems()}
                  onDelete={handleRemoveTechnicalReportItem}
                />
              )}
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between pt-3 pb-1 border-b border-neutral-150 mb-4">
                <span className="text-sm font-semibold tracking-tight">Výrobky</span>
                {permission === 2 && data && (
                  <div className="flex items-center gap-1">
                    <button
                      className="h-7 w-7 rounded flex items-center justify-center text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50 transition-colors"
                      data-testid="typemark-add-reference-product-btn"
                      onClick={() => setVisibleAddReferenceProductDialog(true)}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                )}
              </div>
              {data && isEmptyArray(data.reference_product_set) && (
                <DataTable
                  columns={[
                    { title: 'Kód', key: 'code' },
                    { title: 'Název', key: 'title' },
                    { title: 'Popis', key: 'description' },
                    { title: 'id', key: 'id' },
                  ]}
                  data={data.reference_product_set || []}
                  onDelete={handleRemoveReferenceProduct}
                />
              )}
            </div>

            {!isMultiMode && (
              <div className="mt-4">
                <div className="flex items-center justify-between pt-3 pb-1 border-b border-neutral-150 mb-4">
                  <span className="text-sm font-semibold tracking-tight">Instance</span>
                </div>
                {loadingInstances ? (
                  <div className="w-full flex justify-center items-center py-4">
                    <SpinnerOverlay />
                  </div>
                ) : (
                  <InstanceTable id={activeItemId} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <PickValueWithClassificationModal
        multiple
        sessionKey={pickerTarget.sessionKey}
        alreadySelectedValues={existingPickerItems(data, 'propertySets', isMultiMode)}
        visible={visibleAddPropertySetDialog}
        url="left-panel-set-property-definition/"
        pickerUrl="set-property-definition/left-panel-picker/"
        onClose={() => setVisibleAddPropertySetDialog(false)}
        onSubmit={handleAddPropertySet}
      />
      <PickValueWithClassificationModal
        multiple
        sessionKey={pickerTarget.sessionKey}
        alreadySelectedValues={existingPickerItems(data, 'technicalReportSets', isMultiMode)}
        visible={visibleAddTechnicalReportItemSetDialog}
        url="left-panel-set-technical-report-item/"
        pickerUrl="set-technical-report-item/left-panel-picker/"
        onClose={() => setVisibleAddTechnicalReportItemSetDialog(false)}
        onSubmit={handleAddTechnicalReportItemSet}
      />
      <PickValueWithClassificationModal
        multiple
        sessionKey={pickerTarget.sessionKey}
        alreadySelectedValues={existingPickerItems(data, 'properties', isMultiMode)}
        visible={visibleAddPropertyDialog}
        url="left-panel-property-definition/"
        pickerUrl="property-definition/left-panel-picker/"
        onClose={() => setVisibleAddPropertyDialog(false)}
        onSubmit={handleAddProperty}
      />
      <PickValueWithClassificationModal
        multiple
        sessionKey={pickerTarget.sessionKey}
        alreadySelectedValues={existingPickerItems(data, 'technicalReports', isMultiMode)}
        visible={visibleAddPTechnicalReportItemDialog}
        url="left-panel-technical-report-item/"
        pickerUrl="technical-report-item/left-panel-picker/"
        showDetail
        onClose={() => setVisibleAddPTechnicalReportItemDialog(false)}
        onSubmit={handleAddTechnicalReportItem}
      />
      <PickValueWithClassificationModal
        multiple
        sessionKey={pickerTarget.sessionKey}
        alreadySelectedValues={existingPickerItems(data, 'products', isMultiMode)}
        visible={visibleAddReferenceProductDialog}
        url="left-panel-product/"
        pickerUrl="product/left-panel-picker/"
        onClose={() => setVisibleAddReferenceProductDialog(false)}
        onSubmit={handleAddReferenceProduct}
      />

      <TemplateModal
        visible={templatesModalOpen}
        classificationId={data?.classification?.id}
        onClose={() => setTemplatesModalOpen(false)}
        onSubmit={handlePickTemplate}
      />

      <SolutionPickerModal
        visible={visibleAddComponent}
        onClose={() => setVisibleAddComponent(false)}
        onSubmit={handleAddComponent}
        classificationId={activeComponentId}
      />

      <SolutionPropertyDefinitionModal
        propertyId={propertyIdForSetValue}
        visible={visibleAddPropertyValueDialog}
        formProps={data?.property_set?.find(
          (property) => property.property_definition.id === propertyIdForSetValue
        )}
        onClose={() => {
          setVisibleAddPropertyValueDialog(false);
          setPropertyIdForSetValue(null);
        }}
        onSubmit={handleAddPropertyValue}
      />
      <TypeMarkModal
        title="TypeMark"
        visible={visibleBasicEditDialog}
        formProps={data}
        isMultiMode={isMultiMode}
        onSubmit={handleSubmitTypeMarkDialog}
        onClose={handleCloseTypeMarkDialog}
      />
      {visibleColorsDialog && (
        <ColorsModal
          title="Vzhledy"
          visible={visibleColorsDialog}
          formProps={data}
          onSubmit={handleSubmitColorsDialog}
          onClose={() => setVisibleColorsDialog(false)}
        />
      )}
    </>
  );
}
